import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, Trophy, BookOpen, Zap, Target, Shield, TrendingUp } from 'lucide-react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// 牌コンポーネント
const TileComponent = ({ tile, onClick, selected, disabled, small = false }) => {
  const getTileDisplay = (tile) => {
    if (!tile) return '?';
    
    if (tile.endsWith('p')) {
      // 筒子 (ピンズ) 🀙-🀡
      const num = parseInt(tile.charAt(0));
      const base = 0x1F019; // 🀙 の Unicode
      return String.fromCodePoint(base + num - 1);
    } else if (tile.endsWith('s')) {
      // 索子 (ソーズ) 🀐-🀘  
      const num = parseInt(tile.charAt(0));
      const base = 0x1F010; // 🀐 の Unicode
      return String.fromCodePoint(base + num - 1);
    } else if (tile.endsWith('z')) {
      // 字牌: 東南西北白發中
      const honors = {
        1: '🀀', // 東
        2: '🀁', // 南  
        3: '🀂', // 西
        4: '🀃', // 北 (使わないが定義)
        5: '🀆', // 白
        6: '🀅', // 發
        7: '🀄'  // 中
      };
      const num = parseInt(tile.charAt(0));
      return honors[num] || '🀫';
    }
    return tile;
  };

  return (
    <div
      className={`tile ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''} ${small ? 'small' : ''}`}
      onClick={onClick}
    >
      <div className="tile-face">
        {getTileDisplay(tile)}
      </div>
    </div>
  );
};

// AI分析パネル
const AIAnalysisPanel = ({ analysis, visible }) => {
  if (!visible || !analysis) return null;

  const getShantenColor = (shanten) => {
    if (shanten === 0) return 'text-green-500';
    if (shanten <= 2) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="ai-panel">
      <div className="ai-header">
        <Brain className="w-5 h-5" />
        <span>AI分析</span>
      </div>
      
      <div className="analysis-content">
        <div className="shanten-info">
          <span className={`shanten-number ${getShantenColor(analysis.shanten)}`}>
            {analysis.shanten}シャンテン
          </span>
        </div>
        
        {analysis.recommended_discard && (
          <div className="recommendation">
            <Target className="w-4 h-4" />
            <span>推奨打牌: </span>
            <TileComponent tile={analysis.recommended_discard} small />
          </div>
        )}
        
        <div className="efficiency-section">
          <TrendingUp className="w-4 h-4" />
          <span>主な受け入れ:</span>
          <div className="wait-tiles">
            {Object.entries(analysis.efficiency_tiles || {}).slice(0, 5).map(([tile, count]) => (
              <div key={tile} className="wait-tile">
                <TileComponent tile={tile} small />
                <span className="count">{count}枚</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="explanation">
          <BookOpen className="w-4 h-4" />
          <p>{analysis.explanation}</p>
        </div>
        
        <div className="safety-section">
          <Shield className="w-4 h-4" />
          <span>安全度ランキング:</span>
          <div className="safety-list">
            {Object.entries(analysis.discard_safety || {})
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([tile, safety]) => (
                <div key={tile} className="safety-item">
                  <TileComponent tile={tile} small />
                  <div className="safety-bar">
                    <div 
                      className="safety-fill"
                      style={{ width: `${safety * 100}%` }}
                    />
                  </div>
                  <span>{Math.round(safety * 100)}%</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ゲームボード
const GameBoard = ({ gameState, onDiscard, analysis, showAnalysis }) => {
  const [selectedTile, setSelectedTile] = useState(null);

  const handleTileClick = (tile) => {
    if (gameState.current_player !== 0) return;
    setSelectedTile(tile);
  };

  const handleDiscard = () => {
    if (selectedTile) {
      onDiscard(selectedTile);
      setSelectedTile(null);
    }
  };

  return (
    <div className="game-board">
      {/* 上家の捨て牌エリア */}
      <div className="opponent-area top">
        <div className="player-info">
          <span>🤖 CPU上家</span>
        </div>
        <div className="discards">
          {gameState.visible_discards['2']?.map((tile, index) => (
            <TileComponent key={index} tile={tile} small disabled />
          ))}
        </div>
      </div>

      {/* 中央エリア */}
      <div className="center-area">
        <div className="game-info">
          <div className="info-item">
            <span>第{gameState.turn}巡目</span>
          </div>
          <div className="info-item">
            <span>残り牌: {gameState.wall_remaining}</span>
          </div>
          <div className="info-item">
            <span>ドラ表示牌:</span>
            {gameState.dora_indicators?.map((tile, index) => (
              <TileComponent key={index} tile={tile} small disabled />
            ))}
          </div>
        </div>
      </div>

      {/* 下家エリア */}
      <div className="opponent-area bottom">
        <div className="player-info">
          <span>🤖 CPU下家</span>
        </div>
        <div className="discards">
          {gameState.visible_discards['1']?.map((tile, index) => (
            <TileComponent key={index} tile={tile} small disabled />
          ))}
        </div>
      </div>

      {/* プレイヤーエリア */}
      <div className="player-area">
        <div className="my-hand">
          {gameState.my_hand?.map((tile, index) => (
            <TileComponent
              key={index}
              tile={tile}
              onClick={() => handleTileClick(tile)}
              selected={selectedTile === tile}
              disabled={gameState.current_player !== 0}
            />
          ))}
        </div>
        
        <div className="action-area">
          {selectedTile && (
            <button 
              className="discard-btn"
              onClick={handleDiscard}
            >
              <Zap className="w-4 h-4" />
              {selectedTile}を切る
            </button>
          )}
        </div>
        
        <div className="my-discards">
          <span>自分の捨て牌:</span>
          <div className="discards">
            {gameState.river?.map((tile, index) => (
              <TileComponent key={index} tile={tile} small disabled />
            ))}
          </div>
        </div>
      </div>

      {/* AI分析パネル */}
      <AIAnalysisPanel analysis={analysis} visible={showAnalysis} />
    </div>
  );
};

// 学習Tips
const LearningTips = ({ tips }) => (
  <div className="learning-tips">
    <h3>
      <BookOpen className="w-5 h-5" />
      学習ポイント
    </h3>
    {tips.map((tip, index) => (
      <div key={index} className="tip-card">
        <div className="tip-header">
          <span className="category">{tip.category}</span>
          <span className="title">{tip.title}</span>
        </div>
        <p className="content">{tip.content}</p>
        {tip.example && (
          <div className="example">
            <span>例: {tip.example}</span>
          </div>
        )}
      </div>
    ))}
  </div>
);

// メインアプリ
function App() {
  const [gameState, setGameState] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(true);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTips();
  }, []);

  const loadTips = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/learning/tips`);
      setTips(response.data.tips);
    } catch (err) {
      console.error('Tips load failed:', err);
    }
  };

  const startNewGame = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/game/start`);
      setGameState(response.data);
      await analyzeCurrentHand(response.data.game_id);
    } catch (err) {
      setError('ゲーム開始に失敗しました');
      console.error('Game start failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const analyzeCurrentHand = async (gameId) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/game/${gameId}/analyze`);
      setAnalysis(response.data);
    } catch (err) {
      console.error('Analysis failed:', err);
    }
  };

  const handleDiscard = async (tile) => {
    if (!gameState) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/game/${gameState.game_id}/discard`, {
        game_id: gameState.game_id,
        tile: tile
      });
      
      // ゲーム状態を更新
      const stateResponse = await axios.get(`${API_BASE_URL}/api/game/${gameState.game_id}/state`);
      setGameState(stateResponse.data);
      
      // 新しい分析を取得
      await analyzeCurrentHand(gameState.game_id);
    } catch (err) {
      setError('打牌に失敗しました');
      console.error('Discard failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!gameState) {
    return (
      <div className="app">
        <div className="welcome-screen">
          <div className="header">
            <Trophy className="w-8 h-8" />
            <h1>三人麻雀AI学習アプリ</h1>
            <p>プロ品質のAI指導で牌効率・危険度・筋理論をマスター</p>
          </div>
          
          <div className="features">
            <div className="feature">
              <Brain className="w-6 h-6" />
              <h3>リアルタイムAI分析</h3>
              <p>每手牌の効率とシャンテンをリアルタイム解析</p>
            </div>
            <div className="feature">
              <Shield className="w-6 h-6" />
              <h3>危険度判定</h3>
              <p>筋理論に基づく科学的安全度計算</p>
            </div>
            <div className="feature">
              <Target className="w-6 h-6" />
              <h3>三人麻雀特化</h3>
              <p>萬子なし・北なしの三人麻雀専用設計</p>
            </div>
          </div>
          
          <button 
            className="start-btn"
            onClick={startNewGame}
            disabled={loading}
          >
            {loading ? '準備中...' : 'ゲーム開始'}
          </button>
          
          <LearningTips tips={tips} />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="game-header">
        <div className="title">
          <Brain className="w-6 h-6" />
          <h2>三人麻雀AI学習</h2>
        </div>
        <div className="controls">
          <button 
            className="toggle-btn"
            onClick={() => setShowAnalysis(!showAnalysis)}
          >
            {showAnalysis ? 'AI分析を隠す' : 'AI分析を表示'}
          </button>
          <button 
            className="new-game-btn"
            onClick={startNewGame}
            disabled={loading}
          >
            新しいゲーム
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <GameBoard 
        gameState={gameState}
        onDiscard={handleDiscard}
        analysis={analysis}
        showAnalysis={showAnalysis}
      />
      
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>処理中...</p>
        </div>
      )}
    </div>
  );
}

export default App;